import { effect, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ChatChannelStore } from '../store/chat-channel-store';
import { getUnmutedUnreadMessagesCount } from '../utils/chat-utils';

const BASE_TITLE = 'Fleet Chaser';
const BADGE_COLOR = '#f43f5e';

@Injectable({ providedIn: 'root' })
export class ChatTabNotificationService {
  private title = inject(Title);
  private document = inject(DOCUMENT);
  private channelStore = inject(ChatChannelStore);

  private badgedFavicons = new Map<HTMLLinkElement, Promise<string | null>>();
  private originalFavicons = new Map<HTMLLinkElement, string>();
  private latestBadgeRequest = 0;

  constructor() {
    effect(() => {
      const unreadCount = getUnmutedUnreadMessagesCount(
        this.channelStore.selectors.channels(),
      );
      this.title.setTitle(
        unreadCount > 0 ? `(${unreadCount}) ${BASE_TITLE}` : BASE_TITLE,
      );
      this.setFaviconBadgeVisible(unreadCount > 0);
    });
  }

  private getFaviconLinks(): HTMLLinkElement[] {
    return Array.from(
      this.document.querySelectorAll<HTMLLinkElement>(
        'link[rel="icon"][type="image/png"], link[rel="shortcut icon"]',
      ),
    );
  }

  private async setFaviconBadgeVisible(visible: boolean): Promise<void> {
    const request = ++this.latestBadgeRequest;
    for (const link of this.getFaviconLinks()) {
      if (!this.originalFavicons.has(link)) {
        this.originalFavicons.set(link, link.href);
      }
      let nextHref = this.originalFavicons.get(link)!;
      if (visible) {
        let badged = this.badgedFavicons.get(link);
        if (!badged) {
          badged = this.drawBadgedFavicon(nextHref);
          this.badgedFavicons.set(link, badged);
        }
        nextHref = (await badged) ?? nextHref;
        if (request !== this.latestBadgeRequest) return;
      }
      if (link.href !== nextHref) {
        link.href = nextHref;
        // Chrome doesn't repaint the tab icon on an in-place href change —
        // the link element has to be re-inserted.
        link.parentNode?.appendChild(link);
      }
    }
  }

  private async drawBadgedFavicon(iconUrl: string): Promise<string | null> {
    try {
      const icon = await this.loadImage(iconUrl);
      const size = icon.naturalWidth || 32;
      const canvas = this.document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.drawImage(icon, 0, 0, size, size);
      const badgeRadius = size * 0.22;
      const badgeCenter = size - badgeRadius;
      context.beginPath();
      // Halo erases the icon under the badge edge so the dot stays visible
      // on both light and dark tab bars.
      context.arc(
        badgeCenter,
        badgeCenter,
        badgeRadius + Math.max(1, size * 0.06),
        0,
        Math.PI * 2,
      );
      context.globalCompositeOperation = 'destination-out';
      context.fill();
      context.globalCompositeOperation = 'source-over';
      context.beginPath();
      context.arc(badgeCenter, badgeCenter, badgeRadius, 0, Math.PI * 2);
      context.fillStyle = BADGE_COLOR;
      context.fill();
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
  }
}
